import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TreeStructure, Users, Hourglass, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function JoinPage() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load invite preview (public endpoint — no auth needed)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/${encodeURIComponent(code)}`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) setPreviewError(formatApiError(e) || "Invite link is invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  // If not authenticated, bounce to login with ?next=
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [loading, user, navigate, location.pathname]);

  const handleJoin = async () => {
    if (!preview?.valid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/groups/join", { invite_code: code });
      if (data.status === "verified") {
        toast.success(`Joined ${preview.group_name}`);
      } else {
        toast.success("Request sent — awaiting admin approval");
      }
      navigate(`/groups/${data.group_id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to join");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen bg-ivory flex items-center justify-center text-sageGray">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-ivory grain-bg flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-10" data-testid="join-logo-link">
          <span className="w-10 h-10 rounded-full bg-forest text-gold flex items-center justify-center">
            <TreeStructure size={22} weight="duotone" />
          </span>
          <span className="font-display text-2xl text-forest tracking-tight">LegacyLink</span>
        </Link>

        <div className="legacy-card p-10 text-center animate-fade-in" data-testid="join-card">
          {previewError ? (
            <>
              <span className="w-14 h-14 mx-auto rounded-full bg-destructive/10 text-destructive flex items-center justify-center mb-4">
                <XCircle size={28} weight="duotone" />
              </span>
              <h1 className="font-display text-2xl text-forest mb-2">Invite not valid</h1>
              <p className="text-sageGray mb-6">{previewError}</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="join-back-button">Back to dashboard</Button>
            </>
          ) : !preview ? (
            <p className="text-sageGray">Loading invite…</p>
          ) : !preview.valid ? (
            <>
              <span className="w-14 h-14 mx-auto rounded-full bg-gold/20 text-forest flex items-center justify-center mb-4">
                <Hourglass size={28} weight="duotone" />
              </span>
              <h1 className="font-display text-2xl text-forest mb-2">This invite has expired</h1>
              <p className="text-sageGray mb-6">Ask an admin of <em>{preview.group_name}</em> to send you a fresh one.</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="join-back-button">Back to dashboard</Button>
            </>
          ) : (
            <>
              <p className="text-sm tracking-[0.22em] uppercase text-forest/70 mb-3">You've been invited</p>
              <h1 className="font-display text-4xl text-forest" data-testid="join-group-name">{preview.group_name}</h1>
              {preview.group_description && <p className="text-sageGray mt-3">{preview.group_description}</p>}
              <div className="gold-divider w-40 mx-auto my-6" />
              <div className="flex items-center justify-center gap-2 text-sm text-sageGray mb-1">
                <Users size={16} weight="duotone" />
                <span data-testid="join-member-count">{preview.member_count} verified {preview.member_count === 1 ? "member" : "members"}</span>
              </div>
              <code className="block font-mono text-xs tracking-widest text-sageGray mb-8" data-testid="join-code-display">code: {preview.code}</code>
              <Button
                onClick={handleJoin}
                disabled={busy}
                size="lg"
                className="bg-forest hover:bg-forestSoft text-ivory rounded-full h-12 px-10"
                data-testid="join-confirm-button"
              >
                {busy ? "Sending request…" : `Request to join ${preview.group_name}`}
              </Button>
              <p className="text-xs text-sageGray mt-4 flex items-center justify-center gap-1.5">
                <CheckCircle size={14} className="text-forest" weight="fill" /> An admin will approve your request.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
